import { ReactNode, useEffect, useMemo } from 'react';

import { GetListsResponseDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import listVector from '@/assets/vectors/list.svg';
import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { usePermissions } from '@/hooks/use-permissions';
import { useModulesStore } from '@/stores/modules.store';

import { columns } from './table-meta';

export function ListsTable({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  onRowClick,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
}: {
  notFoundDisplay: ReactNode;
  data: GetListsResponseDTO['data'];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  onRowClick: (row: GetListsResponseDTO['data'][number]) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
}) {
  const { syncModuleColumns } = useModulesStore();

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.Lists, columns);
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<GetListsResponseDTO['data'][number]>
        data={data}
        columns={columns}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        initialSorting={initialSorting}
        grouping={grouping}
        columnOrder={(columnOrder || []).map((column) => column.id)}
        onRowClick={onRowClick}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
      />
    </div>
  );
}

export function NoListsCreatedDisplay({ openListDrawer }: { openListDrawer: () => void }) {
  const { canManageLists } = usePermissions();
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={listVector} alt="List" className="h-[170px] w-[250px]" />
      <span className="text-xl leading-[30px] font-medium">No lists have been created yet</span>
      {canManageLists && (
        <>
          <div className="flex flex-col gap-0 text-center">
            <span className="text-secondary-text text-sm">
              Create custom lists to standardise the responses in your forms. You can create lists manually or import a CSV.
            </span>
          </div>
          <div className="pointer-events-auto flex gap-1">
            <Button variant="success" onClick={openListDrawer}>
              Create List
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function NoListsFoundFromSearchDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No lists match your search</span>
    </div>
  );
}

export function NoListsFoundFromFiltersDisplay({ openListDrawer, removeColumnFilters }: { openListDrawer: () => void; removeColumnFilters: () => void }) {
  const { canManageLists } = usePermissions();
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No lists match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters{canManageLists ? ' or create a new list' : ''}.</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
        {canManageLists && (
          <Button variant="success" onClick={openListDrawer}>
            Create List
          </Button>
        )}
      </div>
    </div>
  );
}
